import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { Nullable } from 'cng-common/models/utils'
import { requestGeneralReportsData } from 'partner360/features/generalReports/api/requestGeneralReportsData'
import { PARTNER360_GENERAL_REPORTS_NAMESPACE } from 'partner360/features/generalReports/constants'
import { getRoots } from 'partner360/features/generalReports/helpers/getRoots'
import { IReportActualsValueType } from 'partner360/models/report/generalReports/IReportActualsValueType'
import { IReportSettings } from 'partner360/models/report/generalReports/IReportSettings'

import { initialState } from './configs'

const parseActualsValueTypes = (value: Nullable<string>): IReportActualsValueType[] => {
  if (!value) {
    return []
  }

  return parseJson<IReportActualsValueType[]>(value) || []
}

const generalReportsSlice = createSlice({
  name: PARTNER360_GENERAL_REPORTS_NAMESPACE,
  initialState,
  reducers: {
    selectGeneralReportsRoot: (state, { payload }: PayloadAction<string>) => {
      if (state.selectedRoots.includes(payload)) {
        state.selectedRoots = state.selectedRoots.filter((root) => root !== payload)
        state.selectedReport = null
      } else {
        state.selectedRoots = [...state.selectedRoots, payload]
      }
    },
    changeGeneralReportsSearchValue: (state, { payload }: PayloadAction<string>) => {
      state.searchStr = payload
    },
    generalReportsSearch: (state) => {
      const searchValue = state.searchStr.toLowerCase()
      if (state.searchStr.length >= 3) {
        const filtered = state.data.filter(
          (el) =>
            el.Description?.toLowerCase().includes(searchValue) ||
            el.Name?.toLowerCase().includes(searchValue) ||
            el.ParentDescription?.toLowerCase().includes(searchValue) ||
            el.ParentName?.toLowerCase().includes(searchValue)
        )

        state.selectedRoots = getRoots(filtered).map((el) => el.id)
        state.selectedReport = null
      }
    },
    selectGeneralReport: (state, { payload }: PayloadAction<Nullable<string>>) => {
      state.selectedReport = payload
    },
    selectGeneralReportsFYear: (state, { payload }: PayloadAction<number>) => {
      state.selectedFYear = payload
    },
    selectGeneralReportsRtm: (state, { payload }: PayloadAction<string>) => {
      state.selectedRtm = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestGeneralReportsData.pending, (state) => {
        state.fetchStatus = RequestStatus.PENDING
      })
      .addCase(requestGeneralReportsData.rejected, (state) => {
        state.fetchStatus = RequestStatus.REJECTED
      })
      .addCase(requestGeneralReportsData.fulfilled, (state, action) => {
        if (action.payload) {
          const parsed = action.payload.Reports.map<IReportSettings>((item) => ({
            ...item,
            Mappings: parseJson(item.Mappings) || [],
            RTM: parseJson<string[]>(item.RTM) || [],
            ActualsValueTypes: parseActualsValueTypes(item.ActualsValueTypes),
          }))
          state.data = parsed.sort((a, b) => {
            return a.ParentOrderBy - b.ParentOrderBy || a.OrderBy - b.OrderBy
          })

          state.fetchStatus = RequestStatus.FULFILLED
        } else {
          state.fetchStatus = RequestStatus.REJECTED
        }
      })
  },
})

export const {
  selectGeneralReportsRoot,
  selectGeneralReportsRtm,
  selectGeneralReportsFYear,
  selectGeneralReport,
  generalReportsSearch,
  changeGeneralReportsSearchValue,
} = generalReportsSlice.actions

export default generalReportsSlice.reducer
